'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Video,
  MapPin,
  Home,
  ChevronDown,
  Crosshair,
  Loader2,
  Check,
  X,
  Stethoscope,
} from 'lucide-react';
import { usePatientLocation } from '@/hooks/usePatientLocation';
import { doctorsApi, type Specialty } from '@/api';
import { cn } from '@/utils/cn';

interface HeroProps {
  serviceType: 'video' | 'home';
  onServiceTypeChange: (type: 'video' | 'home') => void;
}

type TabModality = 'video' | 'office' | 'home';

const QUICK_CHIPS = [
  'Clínico Geral',
  'Pediatria',
  'Dermatologia',
  'Ginecologia',
  'Cardiologia',
  'Psiquiatria',
  'Ortopedia',
  'Oftalmologia',
];

const POPULAR_LOCATIONS = [
  { id: 'sp', label: 'São Paulo, SP' },
  { id: 'rj', label: 'Rio de Janeiro, RJ' },
  { id: 'bh', label: 'Belo Horizonte, MG' },
  { id: 'cwb', label: 'Curitiba, PR' },
  { id: 'bsb', label: 'Brasília, DF' },
  { id: 'poa', label: 'Porto Alegre, RS' },
  { id: 'ssa', label: 'Salvador, BA' },
  { id: 'cps', label: 'Campinas, SP' },
];

export function Hero({ serviceType, onServiceTypeChange }: HeroProps) {
  const navigate = useNavigate();

  const {
    coords: patientCoords,
    cep: patientCep,
    addressLabel: patientAddressLabel,
    isLocating,
    hasLocation,
    requestGpsLocation,
    setCep,
  } = usePatientLocation();

  // Abas de modalidade: 'video' (Online), 'office' (Presencial), 'home' (Em casa)
  const [activeTab, setActiveTab] = useState<TabModality>(serviceType === 'home' ? 'home' : 'video');

  // Campos da barra de busca
  const [specialtyQuery, setSpecialtyQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState(() => {
    if (patientAddressLabel) return patientAddressLabel;
    if (patientCep) return `CEP: ${patientCep}`;
    return 'São Paulo, SP';
  });

  // Estado do dropdown de Especialidades & Sugestões
  const [specialtiesList, setSpecialtiesList] = useState<Specialty[]>([]);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const specialtyRef = useRef<HTMLDivElement>(null);

  // Estado do dropdown de Localização / CEP / GPS
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [cepInput, setCepInput] = useState(patientCep || '');
  const locationRef = useRef<HTMLDivElement>(null);

  // Carrega catálogo oficial de especialidades da API
  useEffect(() => {
    let isMounted = true;
    doctorsApi
      .getSpecialties()
      .then((data) => {
        if (isMounted && Array.isArray(data)) {
          setSpecialtiesList(data);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  // Lista unificada de especialidades para autocomplete
  const availableSpecialtyNames = useMemo(() => {
    const fromApi = specialtiesList.map((s) => s.name);
    return Array.from(new Set([...QUICK_CHIPS, ...fromApi]));
  }, [specialtiesList]);

  const filteredSpecialties = useMemo(() => {
    const q = specialtyQuery.trim().toLowerCase();
    if (!q) return availableSpecialtyNames.slice(0, 8);
    const matches = availableSpecialtyNames.filter((name) =>
      name.toLowerCase().includes(q)
    );
    return matches.slice(0, 10);
  }, [availableSpecialtyNames, specialtyQuery]);

  // Sincroniza quando a geolocalização ou CEP mudar
  useEffect(() => {
    if (patientAddressLabel) {
      setLocationQuery(patientAddressLabel);
    } else if (patientCep) {
      setLocationQuery(`CEP: ${patientCep}`);
    }
  }, [patientAddressLabel, patientCep]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
      if (specialtyRef.current && !specialtyRef.current.contains(event.target as Node)) {
        setShowSpecialtyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTabChange = (tab: TabModality) => {
    setActiveTab(tab);
    if (tab === 'home') {
      onServiceTypeChange('home');
    } else {
      onServiceTypeChange('video');
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();

    const q = specialtyQuery.trim();
    if (q) {
      params.set('q', q);
      // Se coincidir com alguma especialidade do catálogo, envia também como specialty
      const matched = availableSpecialtyNames.find(
        (s) => s.toLowerCase() === q.toLowerCase()
      );
      if (matched) {
        params.set('specialty', matched);
      }
    }

    const loc = locationQuery.trim();
    if (loc) {
      if (loc.startsWith('CEP:')) {
        const rawCep = loc.replace(/\D/g, '');
        if (rawCep) params.set('cep', rawCep);
      } else {
        params.set('city', loc);
      }
    }

    // Envia coordenadas de geolocalização e CEP para a busca por proximidade calibrada
    if (patientCoords) {
      params.set('lat', patientCoords.lat.toString());
      params.set('lng', patientCoords.lng.toString());
    }
    if (patientCep) {
      params.set('cep', patientCep);
    }

    if (activeTab === 'video') {
      params.set('modality', 'TELEMEDICINA');
      params.set('type', 'video');
    } else if (activeTab === 'office') {
      params.set('modality', 'PRESENCIAL');
      params.set('type', 'office');
    } else if (activeTab === 'home') {
      params.set('modality', 'DOMICILIAR');
      params.set('type', 'home');
    }

    navigate(`/buscar?${params.toString()}`);
  };

  const handleChipClick = (specialty: string) => {
    setSpecialtyQuery(specialty);
    const params = new URLSearchParams();
    params.set('q', specialty);
    params.set('specialty', specialty);

    const loc = locationQuery.trim();
    if (loc) {
      if (loc.startsWith('CEP:')) {
        const rawCep = loc.replace(/\D/g, '');
        if (rawCep) params.set('cep', rawCep);
      } else {
        params.set('city', loc);
      }
    }

    if (patientCoords) {
      params.set('lat', patientCoords.lat.toString());
      params.set('lng', patientCoords.lng.toString());
    }
    if (patientCep) {
      params.set('cep', patientCep);
    }

    if (activeTab === 'video') {
      params.set('modality', 'TELEMEDICINA');
      params.set('type', 'video');
    } else if (activeTab === 'office') {
      params.set('modality', 'PRESENCIAL');
      params.set('type', 'office');
    } else if (activeTab === 'home') {
      params.set('modality', 'DOMICILIAR');
      params.set('type', 'home');
    }

    navigate(`/buscar?${params.toString()}`);
  };

  const handleUseGps = async () => {
    const coords = await requestGpsLocation();
    if (coords) {
      setShowLocationDropdown(false);
    }
  };

  const handleCepSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = cepInput.replace(/\D/g, '');
    if (cleaned.length >= 8) {
      await setCep(cleaned);
      setLocationQuery(`CEP: ${cleaned}`);
      setShowLocationDropdown(false);
    }
  };

  return (
    <section className="relative z-20 overflow-x-clip overflow-y-visible bg-gradient-to-br from-[#0b5bbd] via-[#106ec2] to-[#0d54ad] text-white pt-8 pb-16 sm:pt-10 sm:pb-20 lg:pt-12 lg:pb-10 xl:pb-16">
      {/* Elementos de Iluminação e Sofisticação de Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
        {/* Glow Ciano Suave no Topo */}
        <div className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full bg-sky-400/15 blur-[120px]" />
        {/* Glow Azul Royal à Esquerda */}
        <div className="absolute top-1/4 -left-20 w-[30rem] h-[30rem] rounded-full bg-blue-400/15 blur-[100px]" />
        {/* Grid Sutil de Precisão Médica */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_75%_65%_at_50%_35%,#000_65%,transparent_100%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          {/* Coluna da Esquerda: Textos, Tabs, Busca e Tags */}
          <div className="lg:col-span-7 xl:col-span-7 z-20 text-left pb-6 lg:pb-12">
            {/* Logo Oficial Medikei com Efeito Glow Sofisticado */}
            <div className="mb-5 inline-flex items-center gap-3 px-3.5 py-1.5 rounded-2xl bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 shadow-lg shadow-blue-950/20 transition-all duration-300">
              <img
                src="/images/medikei-logo-white.png"
                alt="Medikei Saúde"
                className="h-8 sm:h-9 w-auto object-contain drop-shadow-sm select-none"
              />
              <span className="h-4 w-[1px] bg-white/25" />
              <span className="text-[11px] sm:text-xs font-semibold text-white/90 tracking-wide">
                Plataforma de Saúde Digital
              </span>
            </div>

            {/* Título Principal */}
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-white leading-tight font-display mb-3">
              Agende sua consulta
            </h1>
            <p className="text-base sm:text-lg text-white/90 font-normal leading-relaxed max-w-2xl mb-7">
              Saúde descomplicada a um clique de você, entre nessa com a Medikei!
            </p>

            {/* Container do Formulário e Abas */}
            <div className="w-full max-w-[720px] lg:max-w-3xl">
              {/* Abas Superiores Integradas */}
              <div
                className="inline-flex items-center rounded-t-2xl bg-white p-1.5 shadow-sm"
                role="tablist"
                aria-label="Modalidade de atendimento"
              >
                {/* Aba Online */}
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'video'}
                  onClick={() => handleTabChange('video')}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all focus:outline-none cursor-pointer ${
                    activeTab === 'video'
                      ? 'bg-[#ebf3ff] text-[#1161ec]'
                      : 'bg-white text-slate-800 hover:text-[#1161ec]'
                  }`}
                >
                  <Video className={`w-4 h-4 ${activeTab === 'video' ? 'text-[#1161ec]' : 'text-slate-800'}`} />
                  <span>Online</span>
                </button>

                {/* Aba Presencial */}
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'office'}
                  onClick={() => handleTabChange('office')}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all focus:outline-none cursor-pointer ${
                    activeTab === 'office'
                      ? 'bg-[#ebf3ff] text-[#1161ec]'
                      : 'bg-white text-slate-800 hover:text-[#1161ec]'
                  }`}
                >
                  <MapPin className={`w-4 h-4 ${activeTab === 'office' ? 'text-[#1161ec]' : 'text-slate-800'}`} />
                  <span>Presencial</span>
                </button>

                {/* Divisor vertical sutil entre Presencial e Em Casa */}
                <div className="w-[1px] h-4 bg-slate-200 mx-1.5" />

                {/* Aba Em Casa */}
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'home'}
                  onClick={() => handleTabChange('home')}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all focus:outline-none cursor-pointer ${
                    activeTab === 'home'
                      ? 'bg-[#ebf3ff] text-[#1161ec]'
                      : 'bg-white text-slate-800 hover:text-[#1161ec]'
                  }`}
                >
                  <Home className={`w-4 h-4 ${activeTab === 'home' ? 'text-[#1161ec]' : 'text-slate-800'}`} />
                  <span>Em casa</span>
                </button>
              </div>

              {/* Caixa Principal de Pesquisa */}
              <form
                onSubmit={handleSearchSubmit}
                className="bg-white rounded-2xl rounded-tl-none p-2 sm:p-2.5 shadow-xl shadow-blue-950/20 flex flex-col md:flex-row items-stretch gap-2 text-slate-800 relative z-30"
              >
                {/* Campo 1: Especialidade, Médico ou Serviço */}
                <div
                  ref={specialtyRef}
                  className="relative flex-1 min-w-0 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl hover:bg-slate-50 border border-slate-200/70 md:border-transparent transition-colors flex items-center gap-2 cursor-pointer"
                  onClick={() => setShowSpecialtyDropdown(true)}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <label htmlFor="search-specialty" className="sr-only">
                      Especialidade, médico ou serviço
                    </label>
                    <input
                      id="search-specialty"
                      type="text"
                      value={specialtyQuery}
                      onChange={(e) => {
                        setSpecialtyQuery(e.target.value);
                        setShowSpecialtyDropdown(true);
                      }}
                      onFocus={() => setShowSpecialtyDropdown(true)}
                      placeholder={
                        activeTab === 'video'
                          ? 'Especialidade, médico ou serviço'
                          : activeTab === 'office'
                          ? 'Especialista para consulta presencial'
                          : 'Médico para visita em casa'
                      }
                      className="w-full min-w-0 text-xs sm:text-sm font-medium text-slate-800 placeholder:text-slate-400 bg-transparent focus:outline-none truncate leading-normal"
                    />
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {specialtyQuery && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSpecialtyQuery('');
                        }}
                        className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-200/60 transition-colors cursor-pointer"
                        title="Limpar campo"
                        aria-label="Limpar campo"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSpecialtyDropdown((prev) => !prev);
                      }}
                      className="p-1 -mr-1 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/60 transition-colors cursor-pointer"
                      title="Ver especialidades"
                      aria-label="Ver especialidades"
                    >
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200',
                          showSpecialtyDropdown && 'rotate-180'
                        )}
                      />
                    </button>
                  </div>

                  {/* Popover / Dropdown de Especialidades */}
                  {showSpecialtyDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-full left-0 right-0 md:right-auto md:w-84 mt-2 bg-white border border-slate-200/90 rounded-2xl shadow-2xl shadow-blue-950/25 p-2 z-50 text-slate-800 max-h-72 overflow-y-auto space-y-0.5 animate-in fade-in slide-in-from-top-2 duration-150"
                    >
                      <div className="flex items-center justify-between px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        <span>{specialtyQuery.trim() ? 'Resultados sugeridos' : 'Especialidades populares'}</span>
                        <span className="text-primary font-semibold">{filteredSpecialties.length} opções</span>
                      </div>
                      {filteredSpecialties.length > 0 ? (
                        filteredSpecialties.map((spec) => (
                          <button
                            key={spec}
                            type="button"
                            onClick={() => {
                              setSpecialtyQuery(spec);
                              setShowSpecialtyDropdown(false);
                            }}
                            className={cn(
                              'w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between cursor-pointer',
                              specialtyQuery.toLowerCase() === spec.toLowerCase()
                                ? 'bg-blue-50 text-[#1161ec] font-bold'
                                : 'text-slate-700 hover:bg-slate-50'
                            )}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <Stethoscope className="w-3.5 h-3.5 text-primary shrink-0" />
                              <span className="truncate">{spec}</span>
                            </span>
                            {specialtyQuery.toLowerCase() === spec.toLowerCase() && (
                              <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-3 text-center text-xs text-slate-500">
                          Nenhuma especialidade exata encontrada. Clique em <strong>Pesquisar</strong> para buscar por termo livre.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Divisor Vertical para Desktop */}
                <div className="hidden md:block w-[1px] bg-slate-200 my-2 shrink-0" />

                {/* Campo 2: Localidade com Popover para GPS e CEP */}
                <div
                  ref={locationRef}
                  className="relative flex-1 min-w-0 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl hover:bg-slate-50 border border-slate-200/70 md:border-transparent transition-colors flex items-center gap-2 cursor-pointer"
                  onClick={() => setShowLocationDropdown(true)}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <label htmlFor="search-location" className="sr-only">
                      Localidade
                    </label>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <input
                        id="search-location"
                        type="text"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        onFocus={() => setShowLocationDropdown(true)}
                        placeholder="Cidade ou estado"
                        className="w-full min-w-0 text-xs sm:text-sm font-medium text-slate-800 placeholder:text-slate-400 bg-transparent focus:outline-none truncate leading-normal"
                      />
                      {hasLocation && (
                        <span
                          className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 ring-2 ring-emerald-500/20"
                          title="Localização por proximidade ativa"
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {locationQuery && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocationQuery('');
                        }}
                        className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-200/60 transition-colors cursor-pointer"
                        title="Limpar localização"
                        aria-label="Limpar localização"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLocationDropdown((prev) => !prev);
                      }}
                      className="p-1 -mr-1 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/60 transition-colors cursor-pointer"
                      title="Opções de localização e CEP"
                      aria-label="Opções de localização e CEP"
                    >
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 text-slate-500 shrink-0 ml-1 transition-transform duration-200',
                          showLocationDropdown && 'rotate-180'
                        )}
                      />
                    </button>
                  </div>

                  {/* Popover de Localização / GPS / CEP */}
                  {showLocationDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-full left-0 right-0 md:left-auto md:right-0 md:w-84 mt-2 bg-white border border-slate-200/90 rounded-2xl shadow-2xl shadow-blue-950/25 p-3 z-50 text-slate-800 space-y-2.5 animate-in fade-in slide-in-from-top-2 duration-150"
                    >
                      {/* Botão de Localização Atual / GPS */}
                      <button
                        type="button"
                        onClick={handleUseGps}
                        disabled={isLocating}
                        className="w-full px-3 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-900 text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer text-left"
                      >
                        {isLocating ? (
                          <Loader2 className="w-4 h-4 animate-spin text-emerald-600 shrink-0" />
                        ) : (
                          <Crosshair className="w-4 h-4 text-emerald-600 shrink-0" />
                        )}
                        <div className="flex-1">
                          <span className="block font-bold">Usar minha localização atual (GPS)</span>
                          <span className="text-[10px] text-emerald-700 font-normal">
                            Busca médicos no seu raio de proximidade
                          </span>
                        </div>
                      </button>

                      {/* Campo Rápido de CEP */}
                      <div className="pt-2 border-t border-slate-100">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          Ou buscar por CEP
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            placeholder="00000-000"
                            maxLength={9}
                            value={cepInput}
                            onChange={(e) => setCepInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleCepSubmit(e);
                              }
                            }}
                            className="flex-1 h-8 px-2.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-slate-50 text-slate-800"
                          />
                          <button
                            type="button"
                            onClick={handleCepSubmit}
                            className="h-8 px-3 bg-[#0560fd] hover:bg-[#0051db] text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                          >
                            OK
                          </button>
                        </div>
                      </div>

                      {/* Capitais Populares */}
                      <div className="pt-2 border-t border-slate-100">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Cidades Principais
                        </span>
                        <div className="space-y-0.5">
                          {POPULAR_LOCATIONS.map((loc) => (
                            <button
                              key={loc.id}
                              type="button"
                              onClick={() => {
                                setLocationQuery(loc.label);
                                setShowLocationDropdown(false);
                              }}
                              className={cn(
                                'w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between',
                                locationQuery === loc.label
                                  ? 'bg-blue-50 text-blue-600 font-bold'
                                  : 'text-slate-700 hover:bg-slate-50'
                              )}
                            >
                              <span>{loc.label}</span>
                              {locationQuery === loc.label && <Check className="w-3.5 h-3.5 text-blue-600" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botão de Ação */}
                <button
                  type="submit"
                  className="h-11 sm:h-auto px-7 py-3.5 bg-[#1161ec] hover:bg-[#004ed6] text-white font-bold text-xs sm:text-sm rounded-xl shadow-md hover:shadow-lg shadow-blue-700/30 flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap active:scale-[0.98] shrink-0"
                >
                  <Search className="w-4 h-4 stroke-[2.5]" />
                  <span>Pesquisar</span>
                </button>
              </form>

              {/* Categorias Mais Buscadas (Chips Translúcidos) */}
              <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-white">
                <span className="font-normal mr-1 text-white/80 text-xs">Mais buscadas:</span>
                {QUICK_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => handleChipClick(chip)}
                    className="px-4 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm transition-all cursor-pointer text-xs font-medium"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Coluna da Direita: Imagem da Médica com o Backdrop Orgânico Exclusivo */}
          <div className="hidden lg:flex lg:col-span-5 xl:col-span-5 relative justify-end items-end self-end pointer-events-none select-none">
            {/* Mancha Orgânica / Blob Exclusiva em Tom Azul Claro (#3e95df) Idêntica à Referência */}
            <div className="absolute -top-12 -right-2 w-[440px] h-[540px] bg-gradient-to-tr from-[#3b92dc] via-[#489fe8] to-[#5db0f4] rounded-[48%_52%_68%_32%/38%_45%_55%_62%] shadow-2xl shadow-blue-900/25 -z-10 transform rotate-[-4deg]" />
            <div className="absolute top-16 right-16 w-[320px] h-[320px] bg-[#6bc3ff]/30 rounded-full blur-2xl -z-10" />

            <img
              src="/images/hero-doctor-v2.png"
              alt="Profissional de saúde Medikei pronta para atender"
              className="w-full max-w-[420px] xl:max-w-[460px] h-auto object-contain object-bottom relative z-10 drop-shadow-2xl translate-y-1"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
